// /dashboard/admin/roles/:id — role detail / edit page.
// Spec: docs/specs/custom-rbac.md §6.3.
//
// System roles render read-only (no Save button, PermissionPicker disabled).
// Custom roles support inline edit of name / description / permissions.

import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, ShieldCheck, Trash2, RefreshCw, ServerCrash } from "lucide-react";
import PageHeader from "../../components/PageHeader.js";
import EmptyState from "../../components/EmptyState.js";
import { useConfirmDialog } from "../../components/ConfirmDialog.js";
import { useDashboardSession } from "../../auth/useDashboardSession.js";
import TenantPickerEmptyState from "../../components/TenantPickerEmptyState.js";
import PermissionPicker from "../../components/PermissionPicker.js";
import {
  getRole,
  updateRole,
  replaceRolePermissions,
  deleteRole,
  friendlyRolesError,
  type RoleDetail as RoleDetailT,
} from "../../lib/rolesApi.js";

type State =
  | { status: "loading" }
  | { status: "ready"; role: RoleDetailT; original: { name: string; description: string; permissions: Set<string> } }
  | { status: "error"; message: string };

export default function RoleDetail(): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const session = useDashboardSession();
  const tenantParam = session.super_admin ? session.acting_tenant_id ?? null : null;
  const nav = useNavigate();
  const dlg = useConfirmDialog();

  const [state, setState] = useState<State>({ status: "loading" });
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [permissions, setPermissions] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setState({ status: "loading" });
    try {
      const role = await getRole(id, tenantParam);
      const perms = new Set(role.permissions);
      setState({
        status: "ready",
        role,
        original: { name: role.name, description: role.description ?? "", permissions: perms },
      });
      setName(role.name);
      setDescription(role.description ?? "");
      setPermissions(new Set(perms));
    } catch (err) {
      setState({ status: "error", message: friendlyRolesError(err) });
    }
  }, [id, tenantParam]);

  useEffect(() => { void load(); }, [load]);

  async function handleSave(): Promise<void> {
    if (state.status !== "ready") return;
    const role = state.role;
    if (role.is_system) return;
    setSubmitting(true);
    try {
      // 1) PATCH name / description if changed.
      const metaChanged =
        name.trim() !== state.original.name ||
        (description.trim() || null) !== (state.original.description || null);
      if (metaChanged) {
        await updateRole(role.id, {
          name: name.trim(),
          description: description.trim() || null,
        }, tenantParam);
      }
      // 2) PUT permissions if changed.
      const permsChanged =
        permissions.size !== state.original.permissions.size ||
        [...permissions].some((p) => !state.original.permissions.has(p));
      if (permsChanged) {
        await replaceRolePermissions(role.id, [...permissions], tenantParam);
      }
      await load();
    } catch (err) {
      await dlg.alert(friendlyRolesError(err), { title: "儲存失敗" });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDelete(): Promise<void> {
    if (state.status !== "ready") return;
    const role = state.role;
    const ok = await dlg.confirm(
      `確認刪除自訂角色「${role.name}」？此操作無法復原。`,
      { destructive: true, okLabel: "刪除" },
    );
    if (!ok) return;
    try {
      await deleteRole(role.id, tenantParam);
      nav("/dashboard/admin/roles");
    } catch (err) {
      await dlg.alert(friendlyRolesError(err), { title: "刪除失敗" });
    }
  }

  if (session.super_admin && !tenantParam) {
    return (
      <>
        <PageHeader title="角色詳細" />
        <TenantPickerEmptyState />
      </>
    );
  }

  if (state.status === "loading") {
    return (
      <>
        <PageHeader title="角色詳細" />
        <div className="text-2xs text-text-muted">載入中…</div>
      </>
    );
  }

  if (state.status === "error") {
    return (
      <>
        <PageHeader title="角色詳細" />
        <EmptyState
          icon={ServerCrash}
          title="無法載入角色"
          description={state.message}
          action={
            <button onClick={() => void load()} className="btn-secondary text-sm">
              <RefreshCw className="h-4 w-4" /> 重試
            </button>
          }
        />
      </>
    );
  }

  const { role } = state;
  const dirty =
    name.trim() !== state.original.name ||
    (description.trim() || null) !== (state.original.description || null) ||
    permissions.size !== state.original.permissions.size ||
    [...permissions].some((p) => !state.original.permissions.has(p));

  return (
    <>
      <PageHeader
        title={role.name}
        description={role.is_system ? "系統預設角色 — 不可編輯權限" : "自訂角色"}
        actions={
          <Link to="/dashboard/admin/roles" className="btn-ghost text-sm">
            <ArrowLeft className="h-4 w-4" /> 返回列表
          </Link>
        }
      />

      <div className="space-y-6">
        {/* Meta panel */}
        <section className="card p-5 space-y-4">
          <div className="flex items-center gap-3">
            <span className={[
              "flex h-10 w-10 items-center justify-center rounded-lg flex-shrink-0",
              role.is_system ? "bg-accent-subtle text-accent" : "bg-bg-raised text-text-secondary border border-border",
            ].join(" ")}>
              <ShieldCheck className="h-5 w-5" />
            </span>
            <div>
              <div className="text-sm font-medium text-text-primary">
                {role.is_system ? "系統預設角色" : "自訂角色"}
                <span className="ml-2 text-2xs text-text-muted font-mono">{role.id}</span>
              </div>
              <div className="text-2xs text-text-muted">
                {role.assigned_user_count} 位使用者已套用
                {role.cloned_from && <> · 複製自 <code className="font-mono">{role.cloned_from}</code></>}
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">名稱</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={role.is_system}
              maxLength={120}
              className="input disabled:opacity-60"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-1">描述</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={role.is_system}
              rows={3}
              className="textarea disabled:opacity-60"
            />
          </div>
        </section>

        {/* Permission picker */}
        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-text-primary">權限</h3>
          <PermissionPicker
            value={permissions}
            onChange={setPermissions}
            disabled={role.is_system}
          />
        </section>

        {/* Save bar */}
        {!role.is_system && (
          <div className="sticky bottom-0 bg-bg-base/95 backdrop-blur py-3 border-t border-border -mx-6 px-6 sm:mx-0 sm:px-0 sm:border-0 sm:bg-transparent sm:py-0">
            <div className="flex items-center justify-between gap-3">
              <span className="text-2xs text-text-muted">
                {dirty ? "有未儲存的變更" : "已儲存"}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={!dirty || submitting}
                  onClick={() => { setName(state.original.name); setDescription(state.original.description); setPermissions(new Set(state.original.permissions)); }}
                  className="btn-ghost text-sm"
                >
                  還原
                </button>
                <button
                  type="button"
                  disabled={!dirty || submitting || !name.trim()}
                  onClick={() => void handleSave()}
                  className="btn-primary text-sm"
                >
                  <Save className="h-4 w-4" /> {submitting ? "儲存中…" : "儲存變更"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Danger zone */}
        {!role.is_system && (
          <section className="card p-5 border-danger/30">
            <h3 className="text-sm font-semibold text-text-primary mb-2">危險操作</h3>
            <p className="text-2xs text-text-muted mb-3">
              刪除前需先將所有使用者移除此角色（目前 {role.assigned_user_count} 位）。
            </p>
            <button
              type="button"
              onClick={() => void handleDelete()}
              disabled={role.assigned_user_count > 0}
              className="btn-secondary text-sm text-danger disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Trash2 className="h-4 w-4" /> 刪除此角色
            </button>
          </section>
        )}
      </div>
    </>
  );
}
